import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable } from 'rxjs';
import moment from 'moment/moment';
import { ChartTheme } from '@nimbus-library/features/charts/models/char-theme.enum';
import { ChartThemeActions } from '@nimbus-library/features/charts/store/chart-theme.actions';
import { ThemeSetting } from '@nimbus-library/features/user-settings/models/enums/theme-setting.enum';
import { CookieService } from '@nimbus-library/features/shared/services/cookies/cookies.service';
import { CommonThemeActions } from './common-theme.actions';

interface ICommonThemeStateModel {
  themeSetting: ThemeSetting;
  theme: ChartTheme;
}

@State<ICommonThemeStateModel>({
  name: 'CommonThemeState',
  defaults: {
    themeSetting: ThemeSetting.Browser,
    theme: ChartTheme.Default,
  },
})
@Injectable()
export class CommonThemeState {
  private readonly themeSettingCookieKey: string = 'user_themeSetting';

  public constructor(private readonly cookieService: CookieService) {}

  @Selector()
  public static themeSetting(state: ICommonThemeStateModel): ThemeSetting {
    return state.themeSetting;
  }

  @Selector()
  public static theme(state: ICommonThemeStateModel): ChartTheme {
    return state.theme;
  }

  @Action(CommonThemeActions.Initialize)
  public initialize({ dispatch }: StateContext<ICommonThemeStateModel>, {}: CommonThemeActions.Initialize): Observable<void> {
    const themeSetting = (this.cookieService.getCookie(this.themeSettingCookieKey) as ThemeSetting) ?? ThemeSetting.Browser;
    return dispatch(new CommonThemeActions.SetThemeSetting(themeSetting));
  }

  @Action(CommonThemeActions.SetThemeSetting)
  public setThemeSetting(
    { dispatch, patchState }: StateContext<ICommonThemeStateModel>,
    { themeSetting }: CommonThemeActions.SetThemeSetting,
  ): Observable<void> {
    if (!themeSetting) {
      return;
    }
    patchState({ themeSetting });
    this.cookieService.setCookie(this.themeSettingCookieKey, themeSetting, moment().add(1, 'year'));
    localStorage.setItem('ThemeSetting', themeSetting);
    const theme = this.getTheme(themeSetting);
    return dispatch(new CommonThemeActions.SetTheme(theme));
  }

  @Action(CommonThemeActions.SetTheme)
  public setTheme(
    { dispatch, patchState }: StateContext<ICommonThemeStateModel>,
    { theme }: CommonThemeActions.SetTheme,
  ): Observable<void> {
    if (!theme) {
      return;
    }
    patchState({ theme });
    return dispatch(new ChartThemeActions.SetTheme(theme));
  }

  private getTheme(themeSetting: ThemeSetting): ChartTheme {
    switch (themeSetting) {
      case ThemeSetting.Browser:
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? ChartTheme.Dark : ChartTheme.Light;
      case ThemeSetting.Light:
        return ChartTheme.Light;
      case ThemeSetting.Dark:
        return ChartTheme.Dark;
      default:
        return ChartTheme.Default;
    }
  }
}
